import React, { Fragment, useEffect, useState, useContext } from 'react'
import {
  BrowserRouter as Router,
  Switch,
  Routes,
  Route,
  Navigate,
  useParams
} from "react-router-dom";

import { Context } from '../context';
import { MainLayout } from './MainLayout';
import { LayoutGrid } from './LayoutGrid';
import { Password } from './Password';
import { Alumni } from './Alumni';
import { Project } from './Project';
import { Main } from './Main';
import { People } from './People';
import { Table } from './Table';
import { Head } from './Head';
import { ProjectPage } from './ProjectPage';
import { Login } from './Login';
import { UserTasksPage } from './UserTasksPage';
import { TestLecturesPage } from './TestLecturesPage';
import { SubtitlesPage } from './SubtitlesPage';

// Read the current user's role from localStorage (2 = admin, 3 = superadmin)
function getUserRole() {
  try {
    const authUser = localStorage.getItem('auth_user');
    if (authUser) {
      return JSON.parse(authUser).role || 1;
    }
  } catch (error) {
    console.error('Error reading user role:', error);
  }
  return 1;
}

// Protected Route wrapper component
function ProtectedRoute({ children, isAuthenticated, requireAdmin = false }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (requireAdmin) {
    const role = getUserRole();
    if (role !== 2 && role !== 3) {
      return <Navigate to="/" replace />;
    }
  }
  return children;
}

// Fallback for unknown paths (replaces the old /:people catch-all that
// silently rendered the People admin page for ANY unmatched URL)
function NotFound() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#f0f2f5',
      gap: 12,
    }}>
      <h1 style={{ margin: 0, fontSize: 48 }}>404</h1>
      <div>Страница не найдена</div>
      <a href="/">На главную</a>
    </div>
  );
}

export function RoutesMain(props) {

  const context = useContext(Context);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // API URL for authentication
  const API_URL = process.env.REACT_APP_API_URL || 'https://translation-manag-server.dev.torsunov.pro';

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const expiresAt = localStorage.getItem('auth_expires');

      if (!token || !expiresAt) {
        setIsAuthenticated(false);
        setIsCheckingAuth(false);
        return;
      }

      // Check if token is expired locally first
      if (new Date(expiresAt) < new Date()) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_expires');
        localStorage.removeItem('auth_user');
        setIsAuthenticated(false);
        setIsCheckingAuth(false);
        return;
      }

      // Validate token with API
      try {
        const response = await fetch(`${API_URL}/auth/validate/${token}`);
        const data = await response.json();

        if (data.valid) {
          setIsAuthenticated(true);
          // Update user data if available
          if (data.user) {
            localStorage.setItem('auth_user', JSON.stringify(data.user));
          }
        } else {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_expires');
          localStorage.removeItem('auth_user');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth validation error:', error);
        // On network error, trust local token if not expired
        setIsAuthenticated(true);
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, []);

  const handleLoginSuccess = (token) => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('auth_token');

    // Call logout API to invalidate session
    if (token) {
      try {
        await fetch(`${API_URL}/auth/logout/${token}`, { method: 'POST' });
      } catch (error) {
        console.error('Logout API error:', error);
      }
    }

    // Clear local storage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_expires');
    localStorage.removeItem('auth_user');
    setIsAuthenticated(false);
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5',
      }}>
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <>

      <Router>
        <Routes>

          <Route
            path="/login"
            element={
              isAuthenticated ?
                <Navigate to="/" replace /> :
                <Login onLoginSuccess={handleLoginSuccess} />
            }
          />

          <Route
            exact path="/project/:projectId"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <ProjectPage onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />


          <Route
            exact path="/"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Table onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            exact path="/user-tasks"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <UserTasksPage onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            exact path="/subtitles"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SubtitlesPage onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            exact path="/test-lectures"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <TestLecturesPage onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            exact path="/people"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} requireAdmin>
                <People onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <NotFound />
              </ProtectedRoute>
            }
          />


          {/*
          <Route
            exact path="/:alumni/:project"
            element={<>
              <MainLayout>
                <Password />
                <Project />
              </MainLayout>
            </>
            }
          /> */}



        </Routes>


      </Router>
      {/* <img src="https://app.netlify.com/access-control/bb-api/api/v1/badges/4cc4bc09-3e55-4e95-a81f-c4af42413728/deploy-status" alt="Deploy status badge" height={20} /> */}

    </>
  )
}
